import { MktButton, MktHero, MktSection } from "../components/marketing";
import { Seo } from "../lib/seo";

/**
 * Public /contact page.
 *
 * The real support desk lives inside the app at /app/support and needs a signed-in
 * session, because a case has to belong to an account before anyone can be shown it.
 * This PUBLIC page therefore only explains how to reach support and offers a sign-in
 * CTA plus a plain mailto — it posts to NO backend endpoint of its own.
 *
 * The email address stays here on purpose. Part 10.2 replaces email as the NORMAL
 * support channel for customers who are signed in; it does not remove the address
 * someone needs before they have an account, or when they have lost access to one.
 * See docs/support/SUPPORT_EMAIL_RETENTION.md.
 */
const SUPPORT_EMAIL = "post@okmedia.live";

export function ContactPage(): JSX.Element {
  return (
    <>
      <Seo
        title="Contact ODIN"
        description="Get in touch with the ODIN team at OKMedia. We read every message and reply personally."
        path="/contact"
      />
      <MktHero
        kicker="Contact"
        title="Get in touch with the ODIN team"
        subhead="However you reach us, we keep support calm and human. There's no chatbot maze and no pressure."
      />

      <MktSection kicker="Support" title="Two ways to reach us">
        <div className="mkt-grid">
          <div className="mkt-card">
            <h3>Open a support request</h3>
            <p>
              If you have an ODIN account, this is the better route. Sign in and open a support
              conversation from inside your workspace: it is stored against your account, gets a
              reference, and shows you its status — so you are never left wondering whether a message
              arrived.
            </p>
            <div className="mkt-hero__actions">
              <MktButton to="/login" variant="primary">
                Sign in to open a support request
              </MktButton>
            </div>
          </div>
          <div className="mkt-card">
            <h3>Email us</h3>
            <p>
              No account yet, locked out of one, or reporting something about ODIN rather than asking for
              help with it? Email us. We also use this address for security reports, privacy requests and
              formal notice.
            </p>
            <div className="mkt-hero__actions">
              <MktButton href={`mailto:${SUPPORT_EMAIL}`} variant="secondary">
                {SUPPORT_EMAIL}
              </MktButton>
            </div>
          </div>
        </div>
      </MktSection>

      <MktSection kicker="Good to know" title="What to expect">
        <p className="mkt-section__lead">
          ODIN is rolling out carefully, so response times vary and we make no uptime promises. For the
          platform's current state, check the status page — it's the fastest way to see whether something
          is already known.
        </p>
        <div className="mkt-hero__actions">
          <MktButton to="/status" variant="secondary">
            View platform status
          </MktButton>
        </div>
      </MktSection>
    </>
  );
}
